import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { parseDate, getLocalTimeZone } from "@internationalized/date";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import {
  Snackbar,
  Alert,
  IconButton,
  Box,
  FormControl,
  MenuItem,
  Select,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DatePickerField from "../../../common/DatePickerDashboard";
import NoDataFound from "../../../common/NoDataFound";
import { 
  deleteTaskActionById, 
  getClientCustomerId, 
  getCustomerFromPolicyAction,
  getTypeOfTaskOpenListAction 
} from "../../../redux/actions/taskActions";
import { 
  fetchFilterTaskTypeAction, 
  fetchTaskTypeAction 
} from "../../../redux/actions/dashboardAction";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../loader/loader";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import TaskCalendar from "./TaskCalendar";
import ReusableTable from "../../../common/ReusableTable";

const SnackbarComponent = ({ open, message, severity, onClose }) => (
  <Snackbar
    open={open}
    autoHideDuration={3000}
    onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
  >
    <Alert onClose={onClose} severity={severity} sx={{ width: "100%" }}>
      {message}
    </Alert>
  </Snackbar>
);

const DashboardTypeOfTaskOpen = ({ triggerFetch = false }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedFilter, setSelectedFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isFiltered, setIsFiltered] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [isEditTask, setIsEditTask] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [taskComponentOpen, setTaskComponentOpen] = useState(false);
  const [taskComponentMounted, setTaskComponentMounted] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [selectedEventData, setSelectedEventData] = useState(null);
  const [getTaskData, setGetTaskData] = useState([]);
  const [forceRefreshKey, setForceRefreshKey] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);

  const hasBootedRef = useRef(false);
  const requestInProgressRef = useRef(false);

  const getToday = () => {
    const today = new Date();
    return parseDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`);
  };

  const getFirstOfMonth = () => {
    const today = new Date();
    return parseDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`);
  };

  const formatForForm = (date) => {
    const d = date.toDate(getLocalTimeZone());
    return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const initialFormData = {
    fromDate: formatForForm(getFirstOfMonth()),
    toDate: formatForForm(getToday()),
  };

  const [formData, setFormData] = useState(initialFormData);

  const task_type_data = useSelector((state) => state.dashboardData?.all_task_type?.addTaskList) || {};
  const taskList = task_type_data.content || [];
  const pagination = task_type_data || {};
  const clientCustomer = useSelector((state) => state.task.clientCustomer);
  const taskTypes = useSelector((state) => state?.task?.type_task_list || []);

  const forceDataRefresh = useCallback(() => {
    if (requestInProgressRef.current) {
      return;
    }

    requestInProgressRef.current = true;
    setTableLoading(true);

    let actionPromise;
    
    if (isFiltered) {
      const payload = {
        fromDate: dayjs(formData.fromDate).format("YYYY-MM-DD") || "",
        toDate: dayjs(formData.toDate).format("YYYY-MM-DD") || "",
        taskType: selectedFilter,
        cacheBreaker: `_cb=${Date.now()}`,
      };
      actionPromise = dispatch(fetchFilterTaskTypeAction(payload, page, rowsPerPage));
    } else {
      actionPromise = dispatch(fetchTaskTypeAction(page, rowsPerPage, { cacheBreaker: `_cb=${Date.now()}` }));
    }

    if (actionPromise && typeof actionPromise.finally === 'function') {
      actionPromise.finally(() => {
        requestInProgressRef.current = false;
        setTableLoading(false);
        setForceRefreshKey(prev => prev + 1);
      });
    } else {
      setTimeout(() => {
        requestInProgressRef.current = false;
        setTableLoading(false);
        setForceRefreshKey(prev => prev + 1);
      }, 1000);
    }
  }, [dispatch, page, rowsPerPage, isFiltered, formData, selectedFilter]);

  useEffect(() => {
    if (!triggerFetch || hasBootedRef.current) return;
    hasBootedRef.current = true;
    setTimeout(() => {
      forceDataRefresh();
    }, 100);
  }, [triggerFetch, forceDataRefresh]);

  useEffect(() => {
    if (!hasBootedRef.current) return;
    forceDataRefresh();
  }, [page, rowsPerPage, forceDataRefresh]);

  const handleDateChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  };

  const handleTaskComponentClose = useCallback(() => {
    setTaskComponentOpen(false);
    setTimeout(() => {
      setTaskComponentMounted(false);
      setIsEditTask(false);
      setEditingEventId(null);
      setSelectedEventData(null);
    }, 300);
  }, []);

  const handleEdit = useCallback((task) => {
    if (!task) return;

    try {
      setIsEditTask(true);
      setEditingEventId(task.id);
      setGetTaskData(task);

      let firstName = "";
      let lastName = "";
      if (task.customerName) {
        const nameParts = task.customerName.split(" ");
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(" ");
      }

      const selectedEventData = {
        id: task.id,
        clientCustomerId: task.clientCustomerId,
        policyId: task.policyId,
        dueDate: task.dueDate,
        taskType: task.taskType,
        assignedToName: task.assignedToName,
        assignedTo: task.assignedTo,
        status: task.status,
        taskPriority: task.priority,
        taskDescription: task.taskDescription,
        policyNumber: task.policyId ? { id: task.policyId } : null,
        customer: task.clientCustomerId
          ? {
              id: task.clientCustomerId,
              customerNumber: task.customerNumber || "",
              firstName: firstName || "",
              lastName: lastName || "",
            }
          : null,
      };

      setSelectedEventData(selectedEventData);

      setTimeout(() => {
        setTaskComponentOpen(true);
        setTaskComponentMounted(true);
      }, 50);

      if (task.clientCustomerId) {
        dispatch(getClientCustomerId(task.clientCustomerId));
      }

      const policyIdExists = task.policyId && 
        task.policyId !== null && 
        task.policyId !== undefined && 
        task.policyId !== "" && 
        task.policyId !== "null" && 
        task.policyId !== "undefined";

      if (policyIdExists) {
        dispatch(getCustomerFromPolicyAction(task.policyId))
          .then((response) => {
            if (response?.payload) {
              const policyData = response.payload;
              setSelectedEventData((prev) => ({
                ...prev,
                policyNumber: {
                  id: task.policyId,
                  policyNumber: policyData.policyNumber || task.policyId,
                },
                customer: {
                  id: policyData.clientCustomerId || task.clientCustomerId,
                  firstName: policyData.firstName || firstName,
                  lastName: policyData.lastName || lastName,
                  customerNumber: policyData.customerNumber || task.customerNumber || "",
                },
              }));
            }
          })
          .catch(() => {});
      }
    } catch (error) {
      console.error("Error in handleEdit:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (clientCustomer) {
      setSelectedEventData((prev) =>
        prev
          ? {
              ...prev,
              firstName: clientCustomer.firstName,
              lastName: clientCustomer.lastName,
            }
          : null
      );
    }
  }, [clientCustomer]);

  const handleTaskSubmission = useCallback((formValues) => {
    if (formValues) {
      handleTaskComponentClose();
      setSnackbarMessage("Task updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        forceDataRefresh();
      }, 1000);
    }
  }, [handleTaskComponentClose, forceDataRefresh]);

  const handleTaskDeletion = useCallback(() => {
    if (editingEventId) {
      dispatch(deleteTaskActionById(editingEventId))
        .then(() => {
          setSnackbarMessage("Task deleted successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setTimeout(() => {
            forceDataRefresh();
          }, 1000);
        })
        .catch(() => {
          setSnackbarMessage("Failed to delete task! Please try again.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });

      handleTaskComponentClose();
    }
  }, [editingEventId, dispatch, handleTaskComponentClose, forceDataRefresh]);

  const handleOpenDialog = (id) => {
    setTaskToDelete(id);
    setOpenDialog(true);
  };

  const handleDeleteTask = () => {
    if (!taskToDelete) return;
    dispatch(deleteTaskActionById(taskToDelete))
      .then(() => {
        setSnackbarMessage("Task deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        
        setTimeout(() => {
          forceDataRefresh();
        }, 1000);
      })
      .catch(() => {
        setSnackbarMessage("Failed to delete task! Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setOpenDialog(false);
        setTaskToDelete(null);
      });
  };

  const handleApply = () => {
    const payload = {
      fromDate: dayjs(formData.fromDate).format("YYYY-MM-DD") || "",
      toDate: dayjs(formData.toDate).format("YYYY-MM-DD") || "",
      taskType: selectedFilter,
    };
    
    setTableLoading(true);
    
    if ((formData.fromDate && formData.toDate) || selectedFilter !== "All") {
      dispatch(fetchFilterTaskTypeAction(payload, 0, rowsPerPage))
        .finally(() => {
          setTableLoading(false);
        });
      setIsFiltered(true);
      setPage(0);
    } else {
      setIsFiltered(false);
      setTableLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setTableLoading(true);
    
    if (isFiltered) {
      const payload = {
        fromDate: dayjs(formData.fromDate).format("YYYY-MM-DD") || "",
        toDate: dayjs(formData.toDate).format("YYYY-MM-DD") || "",
        taskType: selectedFilter,
      };
      dispatch(fetchFilterTaskTypeAction(payload, newPage, rowsPerPage))
        .finally(() => {
          setTableLoading(false);
        });
    } else {
      dispatch(fetchTaskTypeAction(newPage, rowsPerPage))
        .finally(() => {
          setTableLoading(false);
        });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setTableLoading(true);
    
    if (isFiltered) {
      const payload = {
        fromDate: dayjs(formData.fromDate).format("YYYY-MM-DD") || "",
        toDate: dayjs(formData.toDate).format("YYYY-MM-DD") || "",
        taskType: selectedFilter,
      };
      dispatch(fetchFilterTaskTypeAction(payload, 0, newRowsPerPage))
        .finally(() => {
          setTableLoading(false);
        });
    } else {
      dispatch(fetchTaskTypeAction(0, newRowsPerPage))
        .finally(() => {
          setTableLoading(false);
        });
    }
  };

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  const handleReset = () => {
    setFormData({
      fromDate: formatForForm(getFirstOfMonth()),
      toDate: formatForForm(getToday()),
    });
    setSelectedFilter("All");
    setIsFiltered(false);
    setPage(0);
    setFieldErrors({});
    setTouchedFields({});
    setTableLoading(false);
    dispatch(fetchTaskTypeAction(0, rowsPerPage))
    .finally(() => setTableLoading(false));
  };

  const handleUpdateTask = (id) => {
    const selectedRow = taskList.find((task) => task.id === id);
    if (selectedRow) {
      handleEdit(selectedRow);
    }
  };

  const getShortDescription = (text) => {
    if (!text) return "";
    const words = text.split(" ");
    if (words.length > 3) return words.slice(0, 3).join(" ") + "...";
    return text;
  };

  const handleSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity || "success");
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const formatDueDate = (value) =>
    value
      ? new Date(value)
          .toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
          .replace(/\//g, "-")
      : "";

  const columns = [
    { id: "taskType", label: "Type of Task", field: "taskType" },
    { id: "assignedToName", label: "Assigned To", field: "assignedToName" },
    { id: "dueDate", label: "Due Date", render: (row) => formatDueDate(row.dueDate) },
    { id: "status", label: "Status", field: "status" },
    { id: "priority", label: "Priority", field: "priority" },
    {
      id: "description",
      label: "Description",
      render: (row) => (
        <Tooltip
          title={row.taskDescription || ""}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "white",
                color: "black",
                boxShadow: 1,
                border: "1px solid #ccc",
                fontSize: "14px",
                fontWeight: "bold",
              },
            },
            arrow: { sx: { color: "white" } },
          }}
        >
          <span>{getShortDescription(row.taskDescription)}</span>
        </Tooltip>
      ),
    },
    {
      id: "actions",
      label: "Action",
      align: "right",
      render: (row) => (
        <>
          <IconButton
            onClick={() => handleUpdateTask(row.id)}
            size={isMobile ? "small" : "medium"}
            sx={{ p: "6px" }}
            aria-label={`edit task ${row.id}`}
          >
            <EditIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <IconButton
            onClick={() => handleOpenDialog(row.id)}
            size={isMobile ? "small" : "medium"}
            aria-label={`delete task ${row.id}`}
          >
            <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </>
      ),
    },
  ];

  const CustomTaskComponent = useMemo(() => {
    const isEditingTask = selectedEventData && selectedEventData.id;

    if (!taskComponentMounted) return null;

    return (
      <TaskCalendar
        taskComponentOpen={taskComponentOpen}
        isEditTask={isEditTask}
        setIsEditTask={setIsEditTask}
        setTaskComponentOpen={setTaskComponentOpen}
        open={taskComponentOpen}
        handleClose={handleTaskComponentClose}
        initialValues={selectedEventData || {}}
        onSubmit={handleTaskSubmission}
        onDelete={handleTaskDeletion}
        isEditing={isEditingTask ? selectedEventData : null}
        editId={editingEventId}
        handleSnackbar={handleSnackbar}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        getTaskData={getTaskData}
      />
    );
  }, [
    taskComponentMounted,
    taskComponentOpen,
    isEditTask,
    selectedEventData,
    editingEventId,
    getTaskData,
    handleTaskComponentClose,
    handleTaskSubmission,
    handleTaskDeletion,
    isLoading,
  ]);

  return (
    <Box>
      <Box
        sx={{
          padding: isMobile ? "8px" : "10px",
          backgroundColor: "#ffffff",
          overflowX: "auto",
          minHeight: "40vh",
          "@media (min-width: 768px) and (max-width: 1024px)": {
            minHeight: "77vh",
            height: "auto",
            overflowY: "auto",
          },
          "@media (min-width: 1024px) and (min-height: 1366px)": {
            height: "auto",
            minHeight: "80vh",
          },
        }}
      >
        <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              alignItems: "flex-start",
              marginBottom: "12px",
              flexWrap: "wrap",
              backgroundColor: "white",
              borderRadius: "8px",
            }}
          >
            {/* From Date */}
            <Box
              sx={{
                width: { xs: "100%", sm: "100%", md: "215px", lg: "215px", xl: "215px" },
                minWidth: "215px",
                minHeight: "85px",
              }}
            >
              <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
                From Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePickerField
                  fieldName="fromDate"
                  formData={formData}
                  handleDateChange={handleDateChange}
                  handleBlur={handleBlur}
                  touchedFields={touchedFields}
                  fieldErrors={fieldErrors}
                  size={isMobile ? "small" : "medium"}
                  sx={{ width: "100%", "& .MuiInputBase-root": { height: "55px" } }}
                  inputProps={{ id: "fromDate", name: "fromDate" }}
                />
              </LocalizationProvider>
            </Box>

            {/* To Date */}
            <Box
              sx={{
                width: { xs: "100%", sm: "100%", md: "215px", lg: "215px", xl: "215px" },
                minWidth: "215px",
                minHeight: "85px",
              }}
            >
              <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
                To Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePickerField
                  fieldName="toDate"
                  formData={formData}
                  handleDateChange={handleDateChange}
                  handleBlur={handleBlur}
                  touchedFields={touchedFields}
                  fieldErrors={fieldErrors}
                  size={isMobile ? "small" : "medium"}
                  sx={{ width: "100%", "& .MuiInputBase-root": { height: "55px" } }}
                  inputProps={{ id: "toDate", name: "toDate" }}
                />
              </LocalizationProvider>
            </Box>

            {/* Task Type Selector */}
            <FormControl
              sx={{
                width: { xs: "100%", sm: "100%", md: "250px", lg: "250px", xl: "250px" },
                minWidth: "250px",
                minHeight: "85px",
              }}
            >
              <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
                Type Of Task Open
              </Typography>
              <Select
                native
                id="task-type-select"
                value={selectedFilter}
                onChange={handleFilterChange}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  height: "55px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    height: "100% !important",
                  },
                }}
              >
                <option value="All">All</option>
                {taskTypes?.length > 0 ? (
                  taskTypes.map((task, index) => (
                    <option key={index} value={task}>
                      {task}
                    </option>
                  ))
                ) : (
                  <option disabled>Loading...</option>
                )}
              </Select>
            </FormControl>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: isMobile ? "100%" : "auto",
                minHeight: "85px",
                alignItems: "flex-end",
                marginTop: "-3px",
                "& button": { flex: isMobile ? 1 : "none", height: "55px", minHeight: "55px" },
              }}
            >
              <Button
                variant="contained"
                onClick={handleApply}
                id="apply-button"
                size={isMobile ? "small" : "medium"}
                disabled={!formData.fromDate || !formData.toDate}
              >
                Apply
              </Button>
              <Button
                id="reset-button"
                variant="outlined"
                onClick={handleReset}
                size={isMobile ? "small" : "medium"}                
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Box>

        {!isFiltered ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <NoDataFound
              discription="Please use the search filters above to fetch and display updated Type Of Task Open data."
              message="After click Apply- results will shown as per selected filter"
            />
          </Box>
        ) : taskList.length === 0 && !tableLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <NoDataFound
              title="No Data Found"
              discription="Please change search filters range where data is available as per defined range"
              message="After click Apply- results will shown as per selected filter"
            />
          </Box>
        ) : (
          <ReusableTable
            columns={columns}
            rows={taskList}
            loading={tableLoading}
            error={undefined}
            LoaderComponent={Loader}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
            totalPages={pagination?.totalPages || 0}
            totalElements={pagination?.totalElements || 0}
            onPageChange={(next) => handleChangePage(null, next)}
            onRowsPerPageChange={handleChangeRowsPerPage}
            getRowId={(row) => `${row.id}-${forceRefreshKey}`}
            size={isMobile ? "small" : "medium"}
            tableSx={{ minWidth: "650px" }}
            containerSx={{ maxHeight: isMobile ? "60vh" : "none", overflow: "auto", width: "100%" }}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDialog}
          onClose={(event, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") { return; } setOpenDialog(false); }}
          aria-labelledby="delete-confirmation-dialog-title"
          aria-describedby="delete-confirmation-dialog-description"
          disableScrollLock={true}
          keepMounted={false}
          hideBackdrop={false}
          style={{ zIndex: 1300 }}
        >
          <DialogTitle
            id="delete-confirmation-dialog-title"
          >
            Delete Confirmation
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ marginTop: "15px" }} id="delete-confirmation-dialog-description">
              Are you sure you want to delete this task? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleDeleteTask} variant="outlined" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Custom Task Component with Dialog Integration */}
        {CustomTaskComponent}

        {/* Snackbar */}
        <SnackbarComponent
          open={snackbarOpen}
          message={snackbarMessage}
          severity={snackbarSeverity}
          onClose={handleCloseSnackbar}
        />
      </Box>
    </Box>
  );
};

export default DashboardTypeOfTaskOpen;